import React, { useMemo, memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import identity from 'lodash/identity';
import {
  Text,
  Input,
  Button,
  Flex,
  PasswordInput,
  Select
} from 'onefaceui-react-components';

import Modal from 'react/components/Modal/Modal';
import { useSelector } from 'react-redux';
import { getResourceTo } from 'react/utils/utils';
import { FormContainer } from './styles';

function IntegrationModal({
  isOpen,
  integrationData,
  title,
  message,
  confirmActionCallback,
  cancelActionCallback
}) {
  const {
    url, credentials, password, enabled, integration
  } = useMemo(
    () => JSON.parse(integrationData),
    [integrationData]
  );

  const { companyName } = credentials;

  const [isInvalidFields, setInvalidFields] = useState(false);

  const [formData, setFormData] = useState({
    url,
    username: credentials.username,
    password,
    type: integration.type,
    ...(companyName && { companyName })
  });

  const { isConnecting, success, error } = useSelector((state) => state.integrations.get('connectionState'));

  const errorMessage = useMemo(
    () => (!isConnecting && error ? error.message : null),
    [error, isConnecting]
  );

  const successMessage = useMemo(
    () => (!isConnecting && !error && success ? success.message : null),
    [error, success, isConnecting]
  );

  const saveIntegrationButtonText = useMemo(() => enabled ? getResourceTo('default.button.save') :
    getResourceTo('IntegrationModal.ConnectIntegration.ConnectButton', integration.name),
    [enabled, integration]
  );

  useEffect(() => {
    const {
      url: formUrl,
      username,
      password: formPassword,
      companyName,
      type
    } = formData;

    const requiredFields = [formUrl, username, formPassword];
    const hasRequiredFields = requiredFields.every(Boolean);

    if (type === 'OD') {
      setInvalidFields(!hasRequiredFields);
    } else {
      setInvalidFields(!(hasRequiredFields && companyName));
    }
  }, [formData]);

  const handleSubmit = () => {
    confirmActionCallback(formData);
  };

  const formFieldProps = (name) => ({
    onChange: (event) => {
      const { value } = event.target || event;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    required: true,
    value: formData[name],
    name
  });

  const ModalBody = (
    <Flex gap={4} flexDirection="column" className="modalBody">
      <Text size="xs" className="modalBodyMessage">
        {message}
      </Text>
      <FormContainer>
        <Select
          label={getResourceTo(
            'IntegrationModal.ConnectIntegration.Type'
          )}
          options={[
            { label: 'OD', value: 'OD' },
            { label: 'LNOS', value: 'LNOS' }
          ]}
          defaultValue={integration.type}
          {...formFieldProps('type')}
        />
        <Input
          type="text"
          label={getResourceTo(
            'IntegrationModal.ConnectIntegration.ServerURL',
            integration.name
          )}
          autoComplete="off"
          disabled={isConnecting}
          className="base-input"
          {...formFieldProps('url')}
        />
        <Input
          type="text"
          label={getResourceTo('IntegrationModal.ConnectIntegration.User')}
          autoComplete="off"
          disabled={isConnecting}
          errorMsg={error}
          className="base-input"
          {...formFieldProps('username')}
        />
        <PasswordInput
          label={getResourceTo('IntegrationModal.ConnectIntegration.Password')}
          autoComplete="off"
          disabled={isConnecting}
          errorMsg={error}
          className="base-input"
          {...formFieldProps('password')}
        />
        {formData.type === 'LNOS' && (
          <Input
            type="text"
            label={getResourceTo(
              'IntegrationModal.ConnectIntegration.CompanyName'
            )}
            autoComplete="off"
            disabled={isConnecting}
            errorMsg={error}
            className="base-input"
            dataTestId="input-companyName"
            {...formFieldProps('companyName')}
          />
        )}
      </FormContainer>

      {successMessage && (
        <div className="modalMessageSuccess">
          <Text size="sm" type="success" weight="bold" className="modalMessageSuccessText">
            <i className="checkIcon fa fa-check" />
            {successMessage ?? 'Sucesso'}
          </Text>
          <Text size="xs">{getResourceTo('IntegrationModal.ConnectIntegration.SuccessInfo')}</Text>
          <Text size="xs">{getResourceTo('IntegrationModal.ConnectIntegration.SuccessSecondaryFeedback')}</Text>
        </div>
      )}
      {errorMessage && (
        <Text size="xs" type="danger" className="modalMessageError">{errorMessage}</Text>
      )}
    </Flex>
  );

  const ModalFooter = (
    <div className={`footerModal ${enabled || success ? 'isConnected' : ''}`}>
      <Button
        variant="secondary"
        onClick={cancelActionCallback}
      >
        {getResourceTo(success ? 'default.button.close' : 'IntegrationModal.ButtonCancel')}
      </Button>

      {!successMessage && (
        <Button
          disabled={isConnecting || isInvalidFields}
          onClick={handleSubmit}
        >
          {isConnecting ? (
            <span className="integration-loading" />
          ) : saveIntegrationButtonText}
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      className="integrationModal"
      isOpen={isOpen}
      title={title}
      crossAxisBtn={false}
      footer={ModalFooter}
      closeModalFn={identity}
    >
      {ModalBody}
    </Modal>
  );
}

IntegrationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  integrationData: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmActionCallback: PropTypes.func.isRequired,
  cancelActionCallback: PropTypes.func.isRequired
};

export default memo(IntegrationModal);
